import { createContext, memo, useContext, useMemo } from 'react';
import OaiApiClient, {
  OaiApiClient as OaiApiClientType,
} from './clients/oai-api/oai-api-client';
import MarketApiClient, {
  MarketApiClient as MarketApiClientType,
} from './clients/market-api/market-api.client';
import GatewayApiClient, {
  GatewayApiClient as GatewayApiClientType,
} from './clients/gateway-api/gateway-api.client';
import CompanyApiClient, {
  CompanyApiClient as CompanyApiClientType,
} from './clients/company-api/company-api.client';
import CloudApiClient, {
  CloudApiClient as CloudApiClientType,
} from './clients/cloud-api/cloud-api.client';
import ToolsApiClient, {
  ToolsApiClient as ToolapiType,
} from './clients/tools-api/tools-api.client';

interface Props {
  children: React.ReactNode;
  getToken?: () => Promise<string | undefined>;
  legacyApiBaseUrl?: string;
  gatewayApiBaseUrl?: string;
  marketApiBaseUrl?: string;
  companyApiBaseUrl?: string;
  cloudApiBaseUrl?: string;
  toolsApiBaseUrl?: string;
}

interface ApiClients {
  oaiApi: OaiApiClientType;
  marketApi: MarketApiClientType;
  gatewayApi: GatewayApiClientType;
  companyApi: CompanyApiClientType;
  cloudApi: CloudApiClientType;
  toolsApi: ToolapiType;
}

const ApiClientsContext = createContext<ApiClients | undefined>(undefined);

export const useApiClients = () => {
  const context = useContext(ApiClientsContext);

  if (context === undefined) {
    throw new Error('useApiClients must be used within a ApiClientsProvider');
  }

  return context;
};

const ApiClientsProvider = ({
  children,
  getToken,
  legacyApiBaseUrl = '',
  gatewayApiBaseUrl = '',
  marketApiBaseUrl = '',
  companyApiBaseUrl = '',
  cloudApiBaseUrl = '',
  toolsApiBaseUrl = '',
}: Props) => {
  const clients = useMemo(() => {
    return {
      oaiApi: OaiApiClient.registerGetToken(legacyApiBaseUrl, getToken),
      marketApi: MarketApiClient.registerGetToken(marketApiBaseUrl, getToken),
      gatewayApi: GatewayApiClient.registerGetToken(
        gatewayApiBaseUrl,
        getToken
      ),
      companyApi: CompanyApiClient.registerGetToken(
        companyApiBaseUrl,
        getToken
      ),
      cloudApi: CloudApiClient.registerGetToken(cloudApiBaseUrl, getToken),
      toolsApi: ToolsApiClient.registerGetToken(toolsApiBaseUrl, getToken),
    };
  }, [
    cloudApiBaseUrl,
    companyApiBaseUrl,
    gatewayApiBaseUrl,
    getToken,
    legacyApiBaseUrl,
    marketApiBaseUrl,
    toolsApiBaseUrl,
  ]);

  return (
    <ApiClientsContext.Provider value={clients}>
      {children}
    </ApiClientsContext.Provider>
  );
};

export default memo(ApiClientsProvider);
